import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Layout } from '../../../widgets/Layout';
import { commonStyles, playerBarStyles, theme } from '../../../shared/lib/styles';
import { playlistAPI } from '../../../shared/api/api';
import { GRADE_LABELS } from '../../../shared/lib/audioposobie/constants';
import {
    getAudioSrc,
    isCommentaryCheckboxDisabled,
    shouldPlayCommentaryFirst,
} from '../../../shared/lib/audioposobie/trackUtils';

const PlaylistDetailPage = () => {
    const { id } = useParams();
    const [playlist, setPlaylist] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [playingIndex, setPlayingIndex] = useState(-1);
    const [currentTrack, setCurrentTrack] = useState(null);
    const [withCommentary, setWithCommentary] = useState(false);
    const [playNext, setPlayNext] = useState(true);
    const audioRef = useRef(null);

    const tracks = playlist?.tracks ?? [];
    const sortedTracks = [...tracks].sort(
        (a, b) => a.position - b.position || a.id - b.id
    );

    const playingItem = playingIndex >= 0 ? sortedTracks[playingIndex] : null;
    const commentaryCheckboxDisabled =
        !playingItem ||
        isCommentaryCheckboxDisabled(
            playingItem.grade,
            playingItem.disk_num,
            playingItem.track_num
        );

    const loadPlaylist = async () => {
        setLoading(true);
        setError('');
        const result = await playlistAPI.getById(id);
        if (result.success) {
            setPlaylist(result.data);
        } else {
            setError('Плейлист не найден');
        }
        setLoading(false);
    };

    useEffect(() => {
        loadPlaylist();
    }, [id]);

    useEffect(() => {
        if (currentTrack?.src && audioRef.current) {
            audioRef.current.src = currentTrack.src;
            audioRef.current.play().catch(() => {});
        }
    }, [currentTrack?.src]);

    const playAtIndex = (index, skipCommentary = false, options = {}) => {
        const item = sortedTracks[index];
        if (!item) return;

        const useCommentary =
            options.useCommentary !== undefined ? options.useCommentary : withCommentary;

        if (
            shouldPlayCommentaryFirst(item.grade, item.disk_num, item.track_num, {
                withCommentary: useCommentary,
                skipCommentary,
            })
        ) {
            setCurrentTrack({
                src: getAudioSrc(item.grade, item.disk_num, item.track_num - 1),
                title: 'Комментарий автора',
                isCommentary: true,
                nextPlaylistIndex: index,
            });
            setPlayingIndex(index);
            return;
        }

        setCurrentTrack({
            src: getAudioSrc(item.grade, item.disk_num, item.track_num),
            title: item.title || `Трек ${item.track_num}`,
        });
        setPlayingIndex(index);
    };

    const handleTrackClick = (index) => {
        if (playingIndex === index && currentTrack && audioRef.current) {
            if (audioRef.current.paused) {
                audioRef.current.play().catch(() => {});
            } else {
                audioRef.current.pause();
            }
            return;
        }
        playAtIndex(index);
    };

    const handleRemoveTrack = async (trackId) => {
        const result = await playlistAPI.removeTrack(id, trackId);
        if (result.success) {
            setMessage('Трек удалён');
            setPlayingIndex(-1);
            setCurrentTrack(null);
            await loadPlaylist();
        } else {
            setError(result.error || 'Ошибка удаления');
        }
    };

    const canGoPrev = playingIndex > 0;
    const canGoNext = playingIndex >= 0 && playingIndex < sortedTracks.length - 1;

    const playerTitle =
        currentTrack?.isCommentary
            ? currentTrack.title
            : playingItem
              ? `${playingItem.title || `Трек ${playingItem.track_num}`} — ${GRADE_LABELS[playingItem.grade] || playingItem.grade}, разд. ${playingItem.disk_num}`
              : '';

    return (
        <Layout>
            <div style={{ ...styles.container, paddingBottom: currentTrack ? 120 : 0 }}>
                <Link to="/playlists" style={styles.backLink}>
                    ← К списку плейлистов
                </Link>

                {loading ? (
                    <div style={commonStyles.loadingContainer}>
                        <div style={commonStyles.loadingSpinner} />
                    </div>
                ) : !playlist ? (
                    <div style={commonStyles.error}>{error || 'Плейлист не найден'}</div>
                ) : (
                    <>
                        <h1 style={styles.title}>{playlist.name}</h1>
                        {message && <div style={commonStyles.success}>{message}</div>}
                        {error && <div style={commonStyles.error}>{error}</div>}

                        <section style={styles.section}>
                            <h2 style={styles.sectionTitle}>Треки в плейлисте</h2>
                            {sortedTracks.length === 0 ? (
                                <p style={styles.empty}>
                                    Плейлист пуст. Добавьте треки из{' '}
                                    <Link to="/audioposobie">Аудиопособия</Link>.
                                </p>
                            ) : (
                                <table style={commonStyles.table}>
                                    <thead>
                                        <tr>
                                            <th style={commonStyles.tableHeader}>#</th>
                                            <th style={commonStyles.tableHeader}>Класс</th>
                                            <th style={commonStyles.tableHeader}>Раздел</th>
                                            <th style={commonStyles.tableHeader}>Трек</th>
                                            <th style={commonStyles.tableHeader}>Название</th>
                                            <th style={commonStyles.tableHeader} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedTracks.map((t, index) => (
                                            <tr
                                                key={t.id}
                                                style={{
                                                    cursor: 'pointer',
                                                    backgroundColor:
                                                        playingIndex === index
                                                            ? theme.colors.tableRowAlt
                                                            : 'transparent',
                                                }}
                                                onClick={() => handleTrackClick(index)}
                                            >
                                                <td style={commonStyles.tableCell}>{index + 1}</td>
                                                <td style={commonStyles.tableCell}>
                                                    {GRADE_LABELS[t.grade] || t.grade}
                                                </td>
                                                <td style={commonStyles.tableCell}>{t.disk_num}</td>
                                                <td style={commonStyles.tableCell}>{t.track_num}</td>
                                                <td style={commonStyles.tableCell}>
                                                    {t.title || `Трек ${t.track_num}`}
                                                </td>
                                                <td style={commonStyles.tableCell}>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveTrack(t.id);
                                                        }}
                                                        style={{
                                                            ...styles.smallBtn,
                                                            color: theme.colors.danger,
                                                        }}
                                                        title="Удалить"
                                                    >
                                                        ×
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </section>
                    </>
                )}

                {currentTrack && (
                    <div style={playerBarStyles.playerFixed}>
                        <div style={playerBarStyles.playerInner}>
                            <button
                                type="button"
                                disabled={!canGoPrev}
                                onClick={() => playAtIndex(playingIndex - 1)}
                                style={{
                                    ...playerBarStyles.playerNavBtn,
                                    ...(!canGoPrev && playerBarStyles.playerNavBtnDisabled),
                                }}
                            >
                                ‹
                            </button>
                            <div style={playerBarStyles.playerContent}>
                                <div
                                    style={{
                                        ...playerBarStyles.playerTitle,
                                        ...(currentTrack.isCommentary && playerBarStyles.playerTitleCommentary),
                                    }}
                                    title={playerTitle}
                                >
                                    {playerTitle}
                                </div>
                                <div style={playerBarStyles.playerCheckboxRow}>
                                    <label style={playerBarStyles.playerCheckbox}>
                                        <input
                                            type="checkbox"
                                            checked={withCommentary}
                                            onChange={() => {
                                                const next = !withCommentary;
                                                setWithCommentary(next);
                                                if (playingIndex >= 0) {
                                                    playAtIndex(playingIndex, false, {
                                                        useCommentary: next,
                                                    });
                                                }
                                            }}
                                            disabled={commentaryCheckboxDisabled}
                                        />
                                        <span
                                            style={
                                                commentaryCheckboxDisabled
                                                    ? playerBarStyles.playerCheckboxDisabled
                                                    : undefined
                                            }
                                        >
                                            С комментарием автора
                                        </span>
                                    </label>
                                    <label style={playerBarStyles.playerCheckbox}>
                                        <input
                                            type="checkbox"
                                            checked={playNext}
                                            onChange={() => setPlayNext(!playNext)}
                                        />
                                        <span>Играть следующий</span>
                                    </label>
                                </div>
                                <div style={playerBarStyles.audioWrap}>
                                <audio
                                    ref={audioRef}
                                    className="landgraf-audio-player"
                                    controls
                                    src={currentTrack.src}
                                    onEnded={() => {
                                        if (
                                            currentTrack.isCommentary &&
                                            currentTrack.nextPlaylistIndex != null
                                        ) {
                                            playAtIndex(currentTrack.nextPlaylistIndex, true);
                                        } else if (playNext && canGoNext) {
                                            playAtIndex(playingIndex + 1);
                                        } else {
                                            setPlayingIndex(-1);
                                            setCurrentTrack(null);
                                        }
                                    }}
                                    style={playerBarStyles.audio}
                                />
                                </div>
                            </div>
                            <button
                                type="button"
                                disabled={!canGoNext}
                                onClick={() => playAtIndex(playingIndex + 1)}
                                style={{
                                    ...playerBarStyles.playerNavBtn,
                                    ...(!canGoNext && playerBarStyles.playerNavBtnDisabled),
                                }}
                            >
                                ›
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

const styles = {
    container: commonStyles.container,
    title: commonStyles.title,
    backLink: {
        display: 'inline-block',
        marginBottom: theme.spacing.lg,
        color: theme.colors.primary,
        textDecoration: 'none',
        fontSize: theme.fontSize.sm,
    },
    section: { marginBottom: theme.spacing.xxl },
    sectionTitle: {
        fontSize: theme.fontSize.xl,
        marginBottom: theme.spacing.md,
        color: theme.colors.textPrimary,
    },
    empty: { color: theme.colors.textSecondary },
    smallBtn: {
        marginRight: theme.spacing.xs,
        padding: '2px 8px',
        cursor: 'pointer',
        border: `1px solid ${theme.colors.borderLight}`,
        borderRadius: theme.borderRadius.sm,
        backgroundColor: theme.colors.bgTertiary,
    },
};

export default PlaylistDetailPage;
